import type {StripeConstructorOptions, Stripe} from '@stripe/stripe-js';
import ReactDOM from 'react-dom';
import type {IMerchantDataContext, useSoftAssertFailed} from '../../context';
import type {ExistingCard} from './DebitCardForm';

export const handleScriptLoad = (
  scriptElement: HTMLScriptElement,
  callback: () => void,
) => scriptElement.addEventListener('load', callback);

export const getFormattedExpiry = (existingCard?: ExistingCard | null) => {
  if (!existingCard) {
    return;
  }
  const month =
    existingCard.exp_month < 10
      ? `0${existingCard.exp_month}`
      : `${existingCard.exp_month}`;
  return `${month} / ${String(existingCard?.exp_year).substr(-2)}`;
};

export const loadStripeJS = (
  locale: StripeConstructorOptions['locale'],
  contentWindow: Window,
  {publishableKey, merchantId}: IMerchantDataContext,
  softAssertFailed: ReturnType<typeof useSoftAssertFailed>,
) => {
  if (!contentWindow.Stripe) {
    softAssertFailed(
      'DebitCardForm: Stripe failed to load',
      'linked_external_account_form',
    );
    return;
  }

  return contentWindow.Stripe(publishableKey, {
    stripeAccount: merchantId,
    locale,
  });
};

export const inIframeBody = (
  content: React.ReactNode,
  contentWindow?: Window | null,
) => {
  return (
    contentWindow && ReactDOM.createPortal(content, contentWindow.document.body)
  );
};

export type StripeJsV2Config = {
  apiVersion: string;
  __v2PrivateBeta: {
    authToken: string;
    stripeContext: string;
  };
};
export const loadStripeJSInWindow = async ({
  locale,
  contentWindow,
  merchantData: {publishableKey, merchantId},
  softAssertFailed,
  stripeJsEndpoint = 'https://js.stripe.com/v3/',
  stripeJsV2Config,
}: {
  locale: StripeConstructorOptions['locale'];
  contentWindow: Window;
  merchantData: {publishableKey: string; merchantId: string};
  softAssertFailed: ReturnType<typeof useSoftAssertFailed>;
  stripeJsEndpoint: string;
  stripeJsV2Config?: StripeJsV2Config;
}): Promise<Stripe | undefined | null> => {
  try {
    if (contentWindow) {
      if (contentWindow.Stripe) {
        const stripe = contentWindow.Stripe(publishableKey, {
          stripeAccount: stripeJsV2Config ? undefined : merchantId,
          locale,
          ...stripeJsV2Config,
        });
        return stripe;
      } else {
        const script = contentWindow.document.createElement('script');
        script.setAttribute('src', stripeJsEndpoint);
        contentWindow.document.head.appendChild(script);
        const stripePromise = new Promise<Stripe>((resolve) => {
          script.addEventListener('load', () => {
            if (!contentWindow.Stripe) {
              softAssertFailed(
                'DebitCardForm: Stripe failed to load',
                'linked_external_account_form',
              );
              return null;
            }

            const stripe = contentWindow.Stripe(publishableKey, {
              stripeAccount: stripeJsV2Config ? undefined : merchantId,
              locale,
              ...stripeJsV2Config,
            });
            resolve(stripe);
          });
        });

        return stripePromise;
      }
    }
  } catch (error) {
    softAssertFailed(
      'DebitCardForm: Stripe failed to load',
      'linked_external_account_form',
    );
    return null;
  }
};
